import { buildRecoverySchedule, type StakingMode } from './staking.ts';

export interface ExposurePreviewInput {
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
  stakingMode: StakingMode;
  baseStake: number;
  targetEpisodeProfit: number;
}

export interface ExposurePreview {
  attempts: number;
  stakes: number[];
  fullStopExposure: number;
  largestWager: number;
  stakingMode: StakingMode;
}

export function validateProfitInputs(input: {
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
  stakingMode: StakingMode;
  baseStake: number;
  targetEpisodeProfit: number;
}): string | null {
  if (!Number.isFinite(input.targetMultiplier) || input.targetMultiplier < 1) {
    return 'Target must be a number ≥ 1.';
  }
  if (!Number.isInteger(input.entryDrought) || input.entryDrought < 1) {
    return 'Entry drought D must be an integer ≥ 1.';
  }
  if (!Number.isInteger(input.stopDrought) || input.stopDrought < input.entryDrought + 1) {
    return 'Stop S must be an integer ≥ D + 1.';
  }
  if (!Number.isFinite(input.baseStake) || input.baseStake <= 0) {
    return 'Base stake must be a number > 0.';
  }
  if (input.stakingMode === 'MARTINGALE' && input.targetMultiplier !== 2) {
    return 'MARTINGALE is available only for target 2x. Use RECOVERY for other targets.';
  }
  if (input.stakingMode === 'RECOVERY' && (!(input.targetEpisodeProfit > 0) || !Number.isFinite(input.targetEpisodeProfit))) {
    return 'Target episode profit must be a number > 0 for RECOVERY.';
  }
  return null;
}

export function exposurePreview(input: ExposurePreviewInput): ExposurePreview {
  const error = validateProfitInputs(input);
  if (error) {
    throw new Error(error);
  }
  const attempts = input.stopDrought - input.entryDrought;
  const schedule = buildRecoverySchedule({
    targetMultiplier: input.targetMultiplier,
    baseStake: input.baseStake,
    targetEpisodeProfit: input.targetEpisodeProfit,
    maxAttempts: attempts,
    mode: input.stakingMode,
  });
  return {
    attempts,
    stakes: schedule.stakes.slice(1),
    fullStopExposure: schedule.prefixStaked[attempts] ?? 0,
    largestWager: schedule.maxStakePrefix[attempts] ?? 0,
    stakingMode: input.stakingMode,
  };
}
